import { Component, OnInit } from '@angular/core';
import { EregistrationsTranslationService } from '../../../services/eregistrations-translation.service';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth/auth-service';
import { GlobalStateProviderService } from '../../../services/global-state-provider.service';
import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import {
  ROLE_PARTB,
  ROLE_SUPER_MARIO,
  ROLE_SUPERINSPECTOR
} from '../../../constants/roles';

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html'
})
export class UserMenuComponent implements OnInit {
  BASE_URL = environment.BASE_URL;
  authAdminUrl: string | null = null;
  bpaUrl: string | null = null;
  statsUrl: string | null = null;
  protected USE_OLD_FE = environment.USE_OLD_FE;
  protected USE_NEW_PART_B_LINK = environment.USE_NEW_PART_B_LINK;
  protected keycloakActive = environment.KEYCLOAK_ACTIVE;
  public userIsSuperinspector: boolean = false;
  public userIsSuperMario: boolean = false;
  public userIsPartB: boolean = false;
  public userIsSysadmin: boolean = false;
  public userHasHomepageAccess: boolean = false;

  /**
   * True when the 'Applicant' (homepage) link should be hidden — i.e. the
   * user is a Part B operator AND does NOT have homepage access. Mirrors
   * `PartBLandingGuard`'s redirect predicate so the menu and the route
   * guard agree on whether `/` is a meaningful destination for this user.
   *
   * `userHasHomepageAccess` is populated from
   * `AuthService.hasHomepageAccess$()` in `ngOnInit` — see
   * `HOMEPAGE_ACCESS_ROLES` for the list of roles that grant homepage
   * access (currently `super_mario`, `Dashboard`).
   */
  public get restrictPartBToOwnServices(): boolean {
    return this.userIsPartB && !this.userHasHomepageAccess;
  }
  private readonly logoutClickedBehaviorSubject = new BehaviorSubject<boolean>(
    false
  );
  private readonly logoutClicked$: Observable<boolean> =
    this.logoutClickedBehaviorSubject.asObservable();

  constructor(
    public authService: AuthService,
    public globalStateProviderService: GlobalStateProviderService,
    public eregistrationsTranslationService: EregistrationsTranslationService
  ) {
    this.logoutClicked$
      .pipe(
        filter((clicked) => clicked),
        switchMap(() => this.authService.logout(window.location.origin))
      )
      .subscribe({});
    if (environment.KEYCLOAK_ACTIVE) {
      this.authAdminUrl = environment.KEYCLOAK_URL;
    } else {
      this.authAdminUrl = environment.PARTC_URL;
    }
    if (environment.BPA_URL) {
      this.bpaUrl = environment.BPA_URL;
    } else {
      const baseDomain = this.BASE_URL.replace(/^https?:\/\/(services\.)?/, '');
      this.bpaUrl = `https://bpa.${baseDomain}`;
    }
    if (environment.STATS_URL) {
      this.statsUrl = environment.STATS_URL;
    } else {
      const baseDomain = this.BASE_URL.replace(/^https?:\/\/(services\.)?/, '');
      this.statsUrl = `https://stats.${baseDomain}`;
    }
  }

  ngOnInit() {
    this.authService.jwt$.subscribe((jwt) => {
      if (jwt) {
        this.getUserIsSuperinspector().subscribe((isSuperinspector) => {
          this.userIsSuperinspector = isSuperinspector;
        });
        this.getUserIsSuperMario().subscribe((isSuperMario) => {
          this.userIsSuperMario = isSuperMario;
        });
        this.getUserIsPartB().subscribe((isPartB) => {
          this.userIsPartB = isPartB;
        });
        this.getUserIsSysadmin().subscribe((isSysadmin) => {
          this.userIsSysadmin = isSysadmin;
        });
        this.authService.hasHomepageAccess$().subscribe((hasHomepage) => {
          this.userHasHomepageAccess = hasHomepage;
        });
      } else {
        this.userIsSuperinspector = false;
        this.userIsSuperMario = false;
        this.userIsPartB = false;
        this.userIsSysadmin = false;
        this.userHasHomepageAccess = false;
      }
    });
  }

  private getUserIsSuperinspector(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.authService.keycloakActive) {
        observer.next(
          this.authService.keycloak.isUserInRole(ROLE_SUPERINSPECTOR)
        );
        observer.complete();
      } else {
        this.authService.cas
          .isUserInRole(ROLE_SUPERINSPECTOR)
          .subscribe((result) => {
            observer.next(result);
            observer.complete();
          });
      }
    });
  }

  private getUserIsSuperMario(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.authService.keycloakActive) {
        observer.next(this.authService.keycloak.isUserInRole(ROLE_SUPER_MARIO));
        observer.complete();
      } else {
        this.authService.cas
          .isUserInRole(ROLE_SUPER_MARIO)
          .subscribe((result) => {
            observer.next(result);
            observer.complete();
          });
      }
    });
  }

  private getUserIsPartB(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.authService.keycloakActive) {
        observer.next(this.authService.keycloak.isUserInRole(ROLE_PARTB));
        observer.complete();
      } else {
        this.authService.cas.isUserInRole(ROLE_PARTB).subscribe((result) => {
          observer.next(result);
          observer.complete();
        });
      }
    });
  }

  private getUserIsSysadmin(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.authService.keycloakActive) {
        observer.next(this.authService.keycloak.isUserInRole('sysadmin'));
        observer.complete();
      } else {
        this.authService.cas.isUserInRole('sysadmin').subscribe((result) => {
          observer.next(result);
          observer.complete();
        });
      }
    });
  }

  public logout() {
    this.logoutClickedBehaviorSubject.next(true);
  }
}
